import React from 'react';
import { Box, Image } from 'rebass';
import { FiDownload } from 'react-icons/fi';
import styled from 'styled-components';
import helpers from 'utils/helpers';
import config from 'config';
import { SESSION_RECORDING_VENDORS } from 'utils/constants';

const VENDOR_ICONS = {
    [SESSION_RECORDING_VENDORS.ZOOM]: { src: 'zoom3.svg', alt: 'Zoom' }
};

const Recordings = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 9px;
`;

const RecordingCard = styled.a`
    display: flex;
    font-size: 12px;
    align-items: center;
    line-height: 100%;
    border: 1px solid #f3f8fe;
    border-radius: 8px;
    background-color: #f3f8fe;
    padding: 9px 10px;
    text-decoration: none;
    color: inherit;

    .download-icon {
        font-size: 12px;
        margin: 4px 4px 4px auto;
        opacity: 0;
        transition: opacity 0.15s;
    }

    &:hover {
        border-color: #4780e6;

        .download-icon {
            opacity: 1;
        }
    }
`;

/**
 * Displays session recording files as a grid of cards
 * @param {Object} props - Component props
 * @param {Array<Object>} props.recordings - Array of recording session objects
 * @param {string} props.recordings[].vendor - Recording vendor (e.g. 'zoom')
 * @param {string} props.recordings[].play_password - Password for accessing recordings
 * @param {Array<Object>} props.recordings[].recording_files - Array of recording files in the session
 * @param {string} props.labelClassName - CSS class name for the label
 * @returns {JSX.Element|null} Session recordings grid or null if no recordings
 */
function SessionRecordings({ recordings, labelClassName }) {
    if (!recordings || recordings.length === 0) {
        return null;
    }

    // Filter recordings to only include files with play_url
    const filteredRecordings = recordings.map(recording => ({
        ...recording,
        recording_files: recording.recording_files?.filter(file => file.play_url) || []
    }));

    return (
        <Box mt={2}>
            <div className={labelClassName}>Session Recordings</div>
            <Recordings>
                {filteredRecordings.map(recording => (
                    <React.Fragment key={recording.id}>
                        {recording.recording_files &&
                            recording.recording_files.length > 0 &&
                            recording.recording_files.map((file, fileIndex) => {
                                const vendorIcon = VENDOR_ICONS[recording.vendor];
                                return (
                                    <RecordingCard
                                        key={file.id || fileIndex}
                                        href={`${file.play_url}?pwd=${recording.play_password}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {vendorIcon && (
                                            <Image
                                                src={`${config.UI_SUFFIX}/${vendorIcon.src}`}
                                                alt={vendorIcon.alt}
                                                height="20px"
                                                sx={{ flexShrink: 0, marginRight: '4px' }}
                                            />
                                        )}
                                        <div className="mx-1">
                                            {helpers.formatRecordingType(file.file_type, file.recording_type)}
                                        </div>
                                        <small className="text-secondary">
                                            ({helpers.humanFileSize(file.file_size)})
                                        </small>
                                        <FiDownload className="download-icon" />
                                    </RecordingCard>
                                );
                            })}
                    </React.Fragment>
                ))}
            </Recordings>
        </Box>
    );
}

export default SessionRecordings;
